/**
 * Financeiro › Conta Corrente › Recebíveis Cielo › Como conciliar.
 *
 * Guia estático (sem chamadas de API) para quem nunca conciliou a Cielo: de onde
 * vem cada número, o que o sistema já faz sozinho, a rotina do dia e — o centro
 * do guia — o que fazer em cada situação que a conferência mostra, com os
 * botões exatos e o que comparar antes de clicar.
 *
 * Os selos reproduzem as mesmas cores e textos das telas de conferência
 * (CieloLedgerPanel, BankReconciliationPanel, ItauStatementReconciliationPanel):
 * se um rótulo mudar lá, mude aqui também.
 */
import React from 'react';
import {
  AlertTriangle,
  BookOpen,
  CalendarCheck,
  CircleHelp,
  ClipboardList,
  FileSpreadsheet,
  GitCompareArrows,
  Landmark,
  ListChecks,
  MessageSquareWarning,
  Workflow,
} from 'lucide-react';
import { Badge, Btn, Callout, GoTo, Section, Situation, Steps } from './guideBlocks';
import { scrollToGuideSection } from './guideUtils';

interface CieloReconciliationGuideProps {
  onOpenSettlement: () => void;
  onOpenBank: () => void;
  /** Só existe quando a conta tem integração com o extrato do Itaú e o usuário tem permissão. */
  onOpenItau?: () => void;
}

const TOC = [
  { id: 'guia-visao', label: '1. Como a conciliação funciona' },
  { id: 'guia-automatico', label: '2. O que o sistema faz sozinho' },
  { id: 'guia-rotina', label: '3. Rotina: diária e semanal' },
  { id: 'guia-importar', label: '4. Importar a planilha da Cielo' },
  { id: 'guia-calendario', label: '5. Ler o calendário e o dia' },
  { id: 'guia-situacoes', label: '6. O que fazer em cada situação' },
  { id: 'guia-extrato', label: '7. Aba Conciliar com o extrato' },
  { id: 'guia-itau', label: '8. Créditos da Cielo na Conciliação Itaú' },
  { id: 'guia-avisos', label: '9. Avisos do processo diário e do Dashboard' },
  { id: 'guia-erros', label: '10. Mensagens de erro' },
  { id: 'guia-glossario', label: '11. Glossário' },
];

// ─── Conteúdo ───────────────────────────────────────────────────

export const CieloReconciliationGuide: React.FC<CieloReconciliationGuideProps> = ({
  onOpenSettlement,
  onOpenBank,
  onOpenItau,
}) => (
  <div className="space-y-4">
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-800">
        <BookOpen size={20} className="text-emerald-600" />
        Como conciliar os recebíveis da Cielo
      </h2>
      <p className="text-sm text-slate-600 mt-1 leading-relaxed">
        Guia para quem vai conferir os recebimentos de cartão pela primeira vez. Leia as seções 1 a 3 uma vez;
        depois, no dia a dia, use a seção 6 como consulta: ela diz, para cada selo que aparece na conferência,
        o que comparar e em qual botão clicar.
      </p>
      <nav className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1">
        {TOC.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => scrollToGuideSection(item.id)}
            className="text-left text-sm text-emerald-700 hover:underline"
          >
            {item.label}
          </button>
        ))}
      </nav>
    </div>

    {/* 1 */}
    <Section id="guia-visao" icon={<GitCompareArrows size={18} />} title="1. Como a conciliação funciona">
      <p>
        Toda venda no cartão vira uma ou mais <strong>parcelas previstas</strong> (a “agenda”): quanto a Cielo deve
        pagar, em que dia e em qual bandeira. Conciliar é confirmar, dia a dia, que três fontes contam a mesma
        história:
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-lg border border-slate-200 p-3">
          <p className="font-medium text-slate-800">① Previsto (agenda)</p>
          <p className="mt-1">O que deveríamos receber, calculado a partir dos pedidos e propostas pagos no cartão.</p>
        </div>
        <div className="rounded-lg border border-slate-200 p-3">
          <p className="font-medium text-slate-800">② Arquivo da Cielo</p>
          <p className="mt-1">
            O que a Cielo diz que pagou (ou vai pagar). Vem da planilha exportada do portal da Cielo.
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 p-3">
          <p className="font-medium text-slate-800">③ Extrato do banco</p>
          <p className="mt-1">O que de fato entrou na conta. Vem do extrato do Itaú (crédito “CIELO …”).</p>
        </div>
      </div>
      <p>
        A Cielo deposita <strong>um valor por dia e por bandeira</strong> (Visa, Mastercard, Elo, Amex), somando
        vendas à vista (“Venda crédito”) e parceladas (“Venda parcelada”). Por isso a comparação é sempre por{' '}
        <strong>data + bandeira</strong>: o crédito do extrato de uma bandeira tem de ser igual à soma da coluna{' '}
        <strong>Arquivo</strong> daquela bandeira no dia.
      </p>
      <p>
        <strong>Onde fica:</strong> menu <Btn>Financeiro</Btn> → <Btn>Conta Corrente</Btn> → escolha a{' '}
        <strong>Empresa</strong> e a <strong>Conta</strong> onde a Cielo deposita → botão <Btn>Recebíveis Cielo</Btn>.
        Aparecem três abas: <Btn>Conferir planilha da Cielo</Btn> (onde as decisões são tomadas),{' '}
        <Btn>Conciliar com o extrato</Btn> (conferência final por dia) e esta, <Btn>Como conciliar</Btn>.
      </p>
      <Callout tone="warn">
        A empresa e a conta escolhidas no topo da Conta Corrente são as que recebem os lançamentos. Antes de
        importar ou baixar qualquer coisa, confira se estão certas: cada empresa tem seu próprio CNPJ na Cielo.
      </Callout>
      <div className="flex flex-wrap gap-2">
        <GoTo onClick={onOpenSettlement}>Abrir Conferir planilha da Cielo</GoTo>
        <GoTo onClick={onOpenBank}>Abrir Conciliar com o extrato</GoTo>
      </div>
    </Section>

    {/* 2 */}
    <Section id="guia-automatico" icon={<Workflow size={18} />} title="2. O que o sistema faz sozinho">
      <p>Todo dia, por volta das 06:00, o processo de sincronização do extrato do Itaú:</p>
      <Steps
        items={[
          <>Busca o extrato do dia anterior e lança os créditos “CIELO …” na conta, com a categoria da Cielo.</>,
          <>
            <strong>Baixa pelo arquivo (TID):</strong> para cada grupo já importado da planilha, se todas as parcelas
            previstas aparecem pagas no arquivo e o crédito está na conta, o grupo é baixado. Na tela aparece como{' '}
            <Badge tone="green">Baixa automática</Badge>.
          </>,
          <>
            <strong>Baixa pela previsão:</strong> para os dias sem planilha, se o crédito do extrato da mesma bandeira
            bate com o previsto, as parcelas são baixadas. Diferença de arredondamento é aceita: até 12 centavos, ou 2
            centavos por parcela em grupos grandes (10 parcelas → até 20 centavos). Na tela aparece como{' '}
            <Badge tone="green">Baixada pelo extrato</Badge>. Os grupos da planilha daquele dia e bandeira fecham junto.
          </>,
          <>
            O que não fecha vira <strong>aviso</strong> na execução do processo (seção 9) e continua aberto na
            conferência, esperando uma decisão sua.
          </>,
        ]}
      />
      <Callout>
        Resultado prático: a maior parte dos dias fica verde sem ninguém clicar em nada. O seu trabalho é cuidar
        das <strong>exceções</strong>: dias amarelos que não fecharam depois do crédito e dias vermelhos.
      </Callout>
    </Section>

    {/* 3 */}
    <Section id="guia-rotina" icon={<ListChecks size={18} />} title="3. Rotina: diária e semanal">
      <p className="font-medium text-slate-800">Todo dia útil (depois das 06:00)</p>
      <Steps
        items={[
          <>
            Abra <Btn>Conferir planilha da Cielo</Btn> e olhe o calendário do mês. Dias passados devem estar verdes.
          </>,
          <>
            Clique em cada dia <Badge tone="amber">Pendente</Badge> ou <Badge tone="red">Divergente</Badge>{' '}
            anterior a hoje. O detalhe do dia abre embaixo da página.
          </>,
          <>Para cada linha aberta, siga o caso correspondente da seção 6.</>,
          <>
            Se a conta tem integração com o Itaú, abra <Btn>Conciliação Itaú</Btn> (seção 8) e confira se não
            ficou nenhum crédito “CIELO …” como <Badge tone="amber">Novo</Badge>.
          </>,
        ]}
      />
      <p className="font-medium text-slate-800 pt-2">Uma vez por semana (e sempre que houver dia sem planilha)</p>
      <Steps
        items={[
          <>Exporte a planilha <strong>Detalhado</strong> no portal da Cielo e importe (seção 4).</>,
          <>
            Abra <Btn>Conciliar com o extrato</Btn>, escolha a semana e clique <Btn>Conciliar</Btn>. A{' '}
            <strong>Diferença</strong> total deve ser R$ 0,00 ou só centavos (seção 7).
          </>,
        ]}
      />
    </Section>

    {/* 4 */}
    <Section id="guia-importar" icon={<FileSpreadsheet size={18} />} title="4. Importar a planilha da Cielo">
      <Steps
        items={[
          <>
            No portal da Cielo, escolha o estabelecimento pelo número de <strong>EC</strong> que aparece no painel de
            importação (“Estabelecimento (EC) Cielo: …”, com botão <Btn>copiar</Btn>). Vá em{' '}
            <strong>Vendas e Recebíveis</strong> e exporte a aba <strong>Detalhado</strong> (arquivo .xlsx ou .csv). Use
            o período sugerido na tela: “gere o próximo arquivo de dd/mm/aaaa até hoje”. Pode incluir datas futuras:
            elas entram como agendadas.
          </>,
          <>
            Em <Btn>Conferir planilha da Cielo</Btn>, clique <Btn>Importar arquivo</Btn> e escolha o arquivo.
          </>,
          <>
            Leia o quadro <strong>Resultado da importação</strong>:
            <ul className="list-disc ml-5 mt-1 space-y-1">
              <li>
                <Badge tone="sky">Nova</Badge> data que ainda não estava carregada; <Badge tone="slate">Sem mudança</Badge>{' '}
                já estava igual; <Badge tone="amber">Registros novos</Badge> o dia ganhou transações;{' '}
                <Badge tone="green">Já aplicada</Badge> o dia já tinha sido baixado; <Badge tone="sky">Agendada</Badge>{' '}
                pagamento futuro.
              </li>
              <li>
                <Badge tone="red">N pedem decisão</Badge>: essas datas já vêm abertas no quadro. Clique{' '}
                <Btn>decidir no dia →</Btn> para ir direto ao detalhe.
              </li>
              <li>
                “Há um buraco na cobertura”: faltam datas entre o último arquivo e este. Exporte e importe um arquivo
                cobrindo exatamente as datas indicadas.
              </li>
              <li>“O arquivo termina hoje”: normal. O dia de hoje será completado na próxima importação.</li>
              <li>
                <Badge tone="green">EC confere</Badge>: as vendas do arquivo são do estabelecimento desta empresa.
              </li>
            </ul>
          </>,
        ]}
      />
      <Callout>
        Reimportar o mesmo período <strong>não duplica nada</strong>: a importação atualiza cada data. Na dúvida se
        um período já foi importado, importe de novo.
      </Callout>
      <Callout tone="warn">
        <p>
          Só o <strong>Detalhado</strong> é aceito. O <strong>Resumo</strong> não traz o TID de cada venda: sem ele a
          baixa automática não fecha o dia, e a importação é recusada com a orientação de exportar o Detalhado.
        </p>
        <p className="mt-1">
          <strong>O Detalhado é uma foto do dia em que foi exportado.</strong> Os pagamentos futuros que ele traz só
          incluem as vendas feitas até ali. Se depois disso entrarem vendas com parcela na mesma data, o painel mostra
          em vermelho “Este dia está com o Detalhado desatualizado” e a data de início: exporte o Detalhado dessa data
          até hoje e importe. Foi o que travou 21/09/2026 (Visa com 8 de 10 vendas e Mastercard com 7 de 10, arquivo
          exportado em 17/08).
        </p>
      </Callout>
    </Section>

    {/* 5 */}
    <Section id="guia-calendario" icon={<CalendarCheck size={18} />} title="5. Ler o calendário e o detalhe do dia">
      <p>Cores do calendário (e da lista ao lado, com o valor pago no dia):</p>
      <ul className="space-y-1">
        <li>
          <Badge tone="green">Conferida</Badge> tudo do dia foi baixado. Nada a fazer.
        </li>
        <li>
          <Badge tone="amber">Pendente</Badge> há grupos abertos, nenhum divergente. Normalmente a baixa automática
          resolve na manhã seguinte ao crédito.
        </li>
        <li>
          <Badge tone="red">Divergente</Badge> pelo menos um grupo com diferença real entre arquivo e previsto.
          Precisa de você.
        </li>
        <li>
          <Badge tone="sky">Agendada</Badge> data futura. Nada a fazer até o dia.
        </li>
      </ul>
      <p>
        Clique no dia (no calendário ou na lista). O detalhe abre no fim da página, com uma linha por{' '}
        <strong>bandeira + tipo</strong> e as colunas:
      </p>
      <ul className="list-disc ml-5 space-y-1">
        <li>
          <strong>Situação</strong>: o selo do grupo (seção 6) e, quando houver, uma explicação em texto pequeno.
        </li>
        <li>
          <strong>Arquivo</strong>: o líquido que a Cielo pagou. O link <Btn>N transação(ões)</Btn> abre as vendas
          do grupo: data da venda, parcela, <strong>Pedido</strong> (link abre o pedido em nova aba), vínculo{' '}
          <Badge tone="green">Prevista</Badge> ou <Badge tone="amber">Fora da agenda</Badge>, bruto, taxa, líquido,
          status na Cielo (Pago / Agendado) e TID/NSU.
        </li>
        <li>
          <strong>Previsto</strong>: o que a agenda esperava e quantas parcelas.
        </li>
        <li>
          <strong>Diferença</strong>: Arquivo − Previsto. Em <span className="text-slate-400 font-medium">cinza</span>{' '}
          é arredondamento da Cielo e não é divergência. O limite é R$ 0,12 por grupo, ou R$ 0,02 por parcela quando
          o grupo tem muitas parcelas; passe o mouse para ver. Em{' '}
          <span className="text-emerald-700 font-medium">verde</span> a Cielo pagou mais; em{' '}
          <span className="text-red-600 font-medium">vermelho</span>, menos.
        </li>
        <li>
          <strong>Ações</strong>: <Btn>Baixar</Btn> e <Btn>Não recebido</Btn> (só em grupos abertos de hoje ou antes).
        </li>
      </ul>
      <div className="rounded-lg border border-slate-200 p-3 space-y-2">
        <p className="font-medium text-slate-800">O que cada botão faz</p>
        <p>
          <Btn>Baixar</Btn> marca <strong>todas</strong> as parcelas previstas do grupo como recebidas e registra o
          valor do arquivo na conta. Se o crédito já está lançado (veio do extrato), ele é{' '}
          <strong>reaproveitado</strong> e nada é lançado em dobro. Aparece uma confirmação com o grupo, o valor e a
          conta: releia e clique <Btn>Confirmar</Btn>, ou <Btn>Cancelar</Btn>.
        </p>
        <p>
          <Btn>Não recebido</Btn> marca <strong>todas</strong> as parcelas previstas do grupo como estornadas, sem
          lançar nada. Use só quando tiver certeza de que o dinheiro não virá (venda cancelada ou estornada).
        </p>
        <p className="text-slate-500">
          Não existe baixa parcial: a decisão vale para o grupo inteiro. Grupo já baixado não pode ser alterado por
          aqui; para desfazer, é preciso excluir o lançamento na Conta Corrente, e isso deve ser combinado com o
          responsável financeiro.
        </p>
      </div>
    </Section>

    {/* 6 */}
    <Section id="guia-situacoes" icon={<ClipboardList size={18} />} title="6. O que fazer em cada situação">
      <p>
        Para todos os casos abaixo, a comparação com o banco é a mesma: abra a <Btn>Conciliação Itaú</Btn> (ou o
        extrato da conta) na mesma data e procure o crédito <strong>CIELO + bandeira</strong> (ex.: “CIELO VISA”,
        “CIELO MAST”). O valor do crédito deve ser igual à soma da coluna <strong>Arquivo</strong> de todas as linhas
        daquela bandeira no dia, incluindo cancelamentos negativos.
      </p>

      <Situation
        badge={
          <>
            <Badge tone="green">Conferida</Badge> <Badge tone="green">Baixa automática</Badge>{' '}
            <Badge tone="green">Baixada pelo extrato</Badge>
          </>
        }
        title="Grupo já baixado"
        looks="Selo verde, com a data da baixa embaixo."
        action="Nada. É só histórico."
      />

      <Situation
        badge={<Badge tone="sky">Agendado</Badge>}
        title="Pagamento futuro ou ainda não pago pela Cielo"
        looks={<>Selo azul; na coluna Ações aparece “libera no dia”.</>}
        action="Nada. No dia do pagamento, com o crédito na conta, a baixa automática resolve."
        avoid="Não tente baixar antes: o sistema recusa baixa de data futura."
      />

      <Situation
        badge={<Badge tone="amber">Confere · aguardando baixa</Badge>}
        title="Arquivo e previsto batem; só falta a baixa"
        looks={<>Selo amarelo; diferença R$ 0,00 ou centavos em cinza.</>}
        compare="Se o crédito da bandeira já está no extrato do dia com o mesmo valor."
        action={
          <>
            <p>
              <strong>Crédito ainda não entrou:</strong> espere; a baixa automática roda na manhã seguinte ao crédito.
            </p>
            <p className="mt-1">
              <strong>Crédito entrou e o grupo continua aberto depois das 06:00 do dia seguinte:</strong> clique{' '}
              <Btn>Baixar</Btn> → confira grupo, valor e conta → <Btn>Confirmar</Btn>. A mensagem verde deve dizer
              “crédito do extrato reaproveitado”.
            </p>
          </>
        }
        avoid="Não use Não recebido: o dinheiro entrou."
      />

      <Situation
        badge={<Badge tone="red">Divergente</Badge>}
        title="A Cielo pagou MAIS que o previsto (vendas fora da agenda)"
        looks={
          <>
            Diferença positiva em verde e o texto:{' '}
            <em className="text-emerald-700">
              “As N parcela(s) prevista(s) estão pagas (R$ …); R$ … são vendas fora da agenda.”
            </em>
          </>
        }
        compare={
          <>
            <p>
              a) O crédito da bandeira no extrato = soma da coluna Arquivo da bandeira no dia.
            </p>
            <p className="mt-1">
              b) Abra <Btn>N transação(ões)</Btn> e veja as vendas <Badge tone="amber">Fora da agenda</Badge>. Se
              tiverem <strong>Pedido</strong>, clique no link e confirme que o pedido existe e foi pago no cartão.
              Costumam ser parcelas de vendas antigas ou pagamento deslocado de data.
            </p>
          </>
        }
        action={
          <>
            Se (a) bate: <Btn>Baixar</Btn> → <Btn>Confirmar</Btn>. Se (a) não bate, trate como “crédito diferente do
            arquivo”, no fim desta seção.
          </>
        }
      />

      <Situation
        badge={<Badge tone="red">Divergente</Badge>}
        title="A Cielo pagou MENOS que o previsto"
        looks={
          <>
            Diferença negativa em vermelho (fora do arredondamento) e, com o Detalhado, o texto{' '}
            <em className="text-amber-700">“Só N de M parcela(s) prevista(s) aparecem pagas no arquivo.”</em>
          </>
        }
        compare={
          <>
            <p>
              a) Abra <Btn>N transação(ões)</Btn> e identifique quais parcelas previstas não vieram (compare com a
              quantidade na coluna Previsto).
            </p>
            <p className="mt-1">
              b) Olhe os dias vizinhos (1 a 3 dias antes e depois) no calendário: a Cielo às vezes paga uma parcela
              em outro dia. Na próxima importação do Detalhado, o sistema move a previsão para a data real pelo TID
              (a linha mostra “parcela k/n ajustada de dd/mm → dd/mm (TID)”).
            </p>
            <p className="mt-1">c) No portal da Cielo, veja o status da venda que falta: cancelada, chargeback ou em aberto.</p>
          </>
        }
        action={
          <>
            <p>
              <strong>Primeiro:</strong> reimporte o Detalhado cobrindo os dias vizinhos e abra o dia de novo; muitas
              vezes a divergência some.
            </p>
            <p className="mt-1">
              <strong>Se persistir</strong>, anote data, bandeira, valor previsto, valor pago e as vendas que faltam, e
              leve ao responsável financeiro <strong>antes</strong> de decidir.
            </p>
          </>
        }
        avoid={
          <>
            Não clique <Btn>Baixar</Btn> “para limpar a tela”: ele marca como recebidas também as parcelas que não
            vieram, e a diferença some da conferência. Este caso também aparece no Dashboard (seção 9).
          </>
        }
      />

      <Situation
        badge={<Badge tone="amber">Só no arquivo</Badge>}
        title="Pagamento (positivo) que a agenda não conhecia"
        looks="O arquivo tem valor e o Previsto é R$ 0,00 (0 parcelas)."
        compare={
          <>
            O crédito da bandeira no extrato inclui esse valor. Abra <Btn>N transação(ões)</Btn> para ver de qual
            venda ou pedido é.
          </>
        }
        action={
          <>
            <p>
              Se o dinheiro entrou: <Btn>Baixar</Btn> → <Btn>Confirmar</Btn> (lança ou reaproveita o crédito).
            </p>
            <p className="mt-1">
              Se o <strong>mesmo valor</strong> aparece como <Badge tone="amber">Só na previsão</Badge> em outra data
              distante (mais de 7 dias), não decida sozinho: é um pagamento fora da data prevista e precisa do
              responsável financeiro.
            </p>
          </>
        }
      />

      <Situation
        badge={<Badge tone="amber">Só no arquivo</Badge>}
        title="Cancelamento de venda (valor negativo)"
        looks={<>Tipo “Cancelamento de venda”, valor negativo no Arquivo, Previsto R$ 0,00.</>}
        compare={
          <>
            A Cielo desconta o cancelamento do depósito do dia. Confira: crédito da bandeira no extrato = soma de todas
            as linhas da bandeira no dia, <strong>com</strong> o valor negativo. Confira também no portal da Cielo (ou
            no pedido) que a venda foi mesmo cancelada.
          </>
        }
        action={
          <>
            <p>
              Quando o Detalhado identifica a venda pelo TID e o crédito da bandeira já está na conta, a{' '}
              <strong>baixa automática</strong> fecha esta linha sozinha na manhã seguinte.
            </p>
            <p className="mt-1">
              Se continuar aberta e o crédito bate: <Btn>Baixar</Btn> → <Btn>Confirmar</Btn>. O crédito do dia é
              reaproveitado e nada novo é lançado. Se não bate, leve ao responsável financeiro.
            </p>
          </>
        }
        avoid="Não use Não recebido: o cancelamento já foi descontado do depósito."
      />

      <Situation
        badge={<Badge tone="amber">Confere · aguardando baixa</Badge>}
        title="Chargeback: o banco pagou MENOS que o arquivo (Detalhado antigo)"
        looks={
          <>
            O dia fica aberto: o Arquivo soma mais que o crédito da bandeira no extrato, e <Btn>Baixar</Btn> responde
            “o crédito … ainda não está na conta”. Caso real: 05/10/2026 MASTER — arquivo R$ 8.492,05, banco R$ 7.932,52;
            a diferença de R$ 559,53 era a parcela 1 do pedido 29732, debitada por chargeback.
          </>
        }
        compare="A diferença costuma ser exatamente o valor de uma parcela já paga de uma venda cancelada ou contestada."
        action={
          <>
            <p>
              Exporte de novo o <strong>Detalhado</strong> cobrindo a data e importe. O arquivo novo traz a linha negativa
              (chargeback/cancelamento) com o TID da venda; o sistema então:
            </p>
            <ul className="list-disc ml-5 mt-1">
              <li>retira da agenda as parcelas futuras dessa venda (o resultado da importação lista quais);</li>
              <li>fecha o dia na baixa automática, porque a soma passa a bater com o banco.</li>
            </ul>
          </>
        }
        avoid={
          <>
            Não use <Btn>Estornar cartão</Btn> no pedido de uma venda em chargeback: a Cielo já tirou o dinheiro.
          </>
        }
      />

      <Situation
        badge={<Badge tone="amber">Só na previsão</Badge>}
        title="Parcela prevista que a Cielo não pagou nesta data"
        looks="Arquivo R$ 0,00 e Previsto com valor."
        compare={
          <>
            Procure o mesmo valor em outras datas e bandeiras (lista do mês e meses vizinhos) e veja no portal da Cielo o
            status da venda.
          </>
        }
        action={
          <>
            <p>
              <strong>Venda cancelada ou estornada (confirmado no portal):</strong> <Btn>Não recebido</Btn> →{' '}
              <Btn>Confirmar</Btn>.
            </p>
            <p className="mt-1">
              <strong>Pago em outra data:</strong> se a diferença for de até 7 dias, reimporte o Detalhado para o sistema
              realocar. Se for maior, não decida sozinho: leve os dois lados (data, bandeira, valor) ao responsável
              financeiro.
            </p>
            <p className="mt-1">
              <strong>Não aparece em lugar nenhum:</strong> espere o próximo arquivo; se continuar, leve ao responsável
              financeiro.
            </p>
          </>
        }
        avoid="Não use Não recebido só porque o valor não caiu hoje: confirme antes que a venda foi cancelada."
      />

      <Situation
        badge={<Badge tone="red">Qualquer selo</Badge>}
        title="O crédito do extrato é diferente do total do arquivo"
        looks="A soma da coluna Arquivo da bandeira no dia não é igual ao crédito CIELO da bandeira no extrato."
        compare={
          <>
            Veja se o extrato do dia foi carregado (seção 8), se o crédito não caiu no dia seguinte e se não foi lançado
            em outra conta ou empresa.
          </>
        }
        action={
          <>
            Não baixe. Se o crédito ainda não foi carregado, espere a sincronização ou use{' '}
            <Btn>Consultar extrato</Btn> na Conciliação Itaú. Se a diferença persistir, leve ao responsável financeiro com
            data, bandeira, valor do arquivo e valor do extrato.
          </>
        }
      />
    </Section>

    {/* 7 */}
    <Section id="guia-extrato" icon={<Landmark size={18} />} title="7. Aba Conciliar com o extrato">
      <p>
        Conferência final, por dia: compara o <strong>previsto</strong> com o que foi <strong>creditado na conta</strong>.
        Não tem botões de decisão; ela aponta os dias que você investiga na aba Conferir planilha da Cielo.
      </p>
      <Steps
        items={[
          <>
            Informe <strong>De</strong> e <strong>Até</strong> (ou use <Btn>Hoje</Btn> / <Btn>Esta semana</Btn>) e
            clique <Btn>Conciliar</Btn>.
          </>,
          <>
            Leia os cartões <strong>Previsto pela agenda</strong>, <strong>Creditado na conta</strong> e{' '}
            <strong>Diferença</strong>. O cartão fica vermelho se houver diferença.
          </>,
          <>
            Na tabela, para cada dia:
            <ul className="list-disc ml-5 mt-1 space-y-1">
              <li>
                <Badge tone="green">Confere</Badge>: nada a fazer.
              </li>
              <li>
                <Badge tone="red">Divergente</Badge> (“o banco creditou MAIS/MENOS do que a agenda previa”): abra o
                mesmo dia na aba Conferir planilha da Cielo e siga a seção 6.
              </li>
              <li>
                <Badge tone="amber">Só no banco</Badge> (“entrou crédito de cartão na conta sem recebível previsto”):
                importe o Detalhado do dia e veja o grupo Só no arquivo.
              </li>
              <li>
                <Badge tone="amber">Só na agenda</Badge> (“havia recebível previsto e nenhum crédito na conta”):
                confira se o extrato do dia foi carregado (seção 8). Se foi e o crédito não veio, siga o caso Só na
                previsão ou Pagou menos.
              </li>
            </ul>
          </>,
          <>
            No quadro <strong>Confirmação dos depósitos pelo banco</strong>, <Badge tone="green">Confirmado</Badge>{' '}
            significa que o extrato trouxe o crédito. <Badge tone="slate">Aguardando</Badge> indica um lançamento que o
            extrato ainda não confirmou: consulte o extrato do período na Conciliação Itaú.
          </>,
        ]}
      />
      <div className="flex flex-wrap gap-2">
        <GoTo onClick={onOpenBank}>Abrir Conciliar com o extrato</GoTo>
      </div>
    </Section>

    {/* 8 */}
    <Section id="guia-itau" icon={<Landmark size={18} />} title="8. Créditos da Cielo na Conciliação Itaú">
      <p>
        Na Conta Corrente, o botão <Btn>Conciliação Itaú</Btn> aparece para contas com integração ativa. Ele traz o
        extrato direto do banco.
      </p>
      <Steps
        items={[
          <>
            Escolha o período (<Btn>Hoje</Btn>, <Btn>Últimos 7 dias</Btn>…) e clique <Btn>Consultar extrato</Btn>.
          </>,
          <>
            Procure as linhas “CIELO …”. Situação <Badge tone="green">Conciliado</Badge>: o crédito já está lançado na
            conta, nada a fazer. <Badge tone="slate">Decidido</Badge>: alguém já decidiu, e o texto embaixo diz o quê.
          </>,
          <>
            Situação <Badge tone="amber">Novo</Badge>: o crédito ainda não virou lançamento. Marque a caixa da linha,
            confira se a <strong>Categoria</strong> sugerida é a da Cielo e clique <Btn>Criar lançamentos (N)</Btn>. Se
            o crédito confere com a agenda, a mensagem verde informa a baixa automática das parcelas de cartão.
          </>,
        ]}
      />
      <Callout tone="warn">
        <p>
          <Btn>Já conciliado</Btn> é só para quando o crédito <strong>já existe como lançamento</strong> na Conta
          Corrente, com o mesmo valor e a mesma data. Ele não cria lançamento.
        </p>
        <p className="mt-1">
          <Btn>Divergência</Btn> registra que o evento não bate com nada, também sem lançamento. Para um crédito da
          Cielo, quase sempre o certo é <Btn>Criar lançamentos</Btn>.
        </p>
        <p className="mt-1">
          Errou? A linha decidida tem <Btn>Desfazer</Btn> e volta a ficar Novo.
        </p>
      </Callout>
      <div className="flex flex-wrap gap-2">
        <GoTo onClick={onOpenItau}>Abrir Conciliação Itaú</GoTo>
      </div>
    </Section>

    {/* 9 */}
    <Section id="guia-avisos" icon={<MessageSquareWarning size={18} />} title="9. Avisos do processo diário e do Dashboard">
      <p>
        <strong>Execução do processo:</strong> menu <Btn>Configuração</Btn> → <Btn>Operações</Btn> →{' '}
        <Btn>Execução de Processos</Btn>. A execução do extrato Itaú mostra “Concluído com alertas” quando há recebível pedindo ação:
        clique <Btn>Ver ações</Btn>. Cada alerta é conferido de novo depois da sincronização, então só aparece o que
        continua em aberto; “Erro parcial” fica reservado para falha de verdade (API do Itaú, certificado). Os alertas de cartão
        são:
      </p>
      <ul className="list-disc ml-5 space-y-2">
        <li>
          <em>“nenhum crédito Cielo na conta em dd/mm para o grupo X — possível falta de pagamento da adquirente”</em>:
          espere um dia útil (pode ser atraso ou extrato ainda não carregado). Se continuar, confira o dia na aba Conferir
          planilha da Cielo e no portal da Cielo.
        </li>
        <li>
          <em>“os créditos Cielo de dd/mm não casam com o previsto do grupo X — divergência”</em>: abra esse dia em
          Conferir planilha da Cielo e siga a seção 6. Diferenças de arredondamento (até 12 centavos, ou 2 por
          parcela) já são aceitas pela baixa automática; se o aviso citar “Detalhado desatualizado”, exporte o
          Detalhado da data indicada.
        </li>
        <li>
          <em>“o depósito de dd/mm (X) já foi conferido pela planilha e estas parcelas ficaram fora dele”</em>: o dia foi
          baixado, mas sobraram parcelas previstas. Abra o dia e trate como Só na previsão.
        </li>
      </ul>
      <p>
        <strong>Dashboard:</strong> o painel de divergências de recebíveis Cielo lista (1) grupos em que a Cielo pagou{' '}
        <strong>menos</strong> que o previsto e (2) parcelas previstas que <strong>não aparecem em nenhum arquivo</strong>{' '}
        (estorno, chargeback, venda que não liquidou). O link do painel leva à conferência. Trate (1) como Pagou menos e
        (2) como Só na previsão.
      </p>
      <p>
        O bloco laranja <strong>“venda(s) de pedido cancelado ainda têm parcelas previstas”</strong> lista pedidos
        cancelados no ERP cujo cartão não foi estornado pelo sistema. Cancelar o pedido não avisa a Cielo, então decida
        venda a venda:
      </p>
      <ul className="list-disc ml-5 space-y-2">
        <li>
          <strong>A cliente deve ser reembolsada e a Cielo ainda não sabe:</strong> abra o pedido (link) e use{' '}
          <Btn>Estornar cartão</Btn>. As parcelas saem da previsão quando o estorno total é aprovado.
        </li>
        <li>
          <strong>A Cielo já desfez a venda (chargeback ou cancelamento feito no portal):</strong> clique{' '}
          <Btn>Não vamos receber</Btn> → <Btn>Confirmar</Btn>. As parcelas saem da previsão de caixa e o histórico do
          pedido registra quem decidiu. Na próxima importação do Detalhado com o débito, isso também acontece sozinho.
        </li>
        <li>
          Pedido cancelado e <strong>refeito</strong> sobre o mesmo pagamento (outro pedido ativo da mesma proposta ou do
          mesmo pedido do site) não aparece aqui: o dinheiro continua vindo.
        </li>
      </ul>
    </Section>

    {/* 10 */}
    <Section id="guia-erros" icon={<AlertTriangle size={18} />} title="10. Mensagens de erro">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-slate-500 uppercase tracking-wider border-b border-slate-200">
              <th className="text-left font-medium py-2 pr-4">Mensagem</th>
              <th className="text-left font-medium py-2">O que fazer</th>
            </tr>
          </thead>
          <tbody className="align-top">
            <tr className="border-b border-slate-100">
              <td className="py-2 pr-4 italic">“a planilha é do CNPJ … e a conta escolhida pertence ao CNPJ …”</td>
              <td className="py-2">
                O arquivo é de outra empresa. Troque a Empresa/Conta no topo da Conta Corrente, ou exporte o arquivo do
                estabelecimento certo.
              </td>
            </tr>
            <tr className="border-b border-slate-100">
              <td className="py-2 pr-4 italic">“a planilha tem vendas do EC … e esta empresa usa o EC …”</td>
              <td className="py-2">
                O arquivo foi exportado de outro estabelecimento. No portal da Cielo, selecione o EC mostrado no painel
                de importação e exporte de novo.
              </td>
            </tr>
            <tr className="border-b border-slate-100">
              <td className="py-2 pr-4 italic">“este é o relatório Resumo da Cielo, que não traz o TID …”</td>
              <td className="py-2">Exporte a aba Detalhado (Recebíveis Detalhado) do mesmo período e importe.</td>
            </tr>
            <tr className="border-b border-slate-100">
              <td className="py-2 pr-4 italic">“o Detalhado de dd/mm está desatualizado (N de M transações)”</td>
              <td className="py-2">
                Aparece no aviso do processo diário e no painel. Exporte o Detalhado a partir da data indicada até hoje e
                importe; a baixa automática fecha o dia na manhã seguinte.
              </td>
            </tr>
            <tr className="border-b border-slate-100">
              <td className="py-2 pr-4 italic">“o grupo … de dd/mm está agendado para o futuro”</td>
              <td className="py-2">Nada a fazer: a baixa só é permitida a partir do dia do pagamento.</td>
            </tr>
            <tr className="border-b border-slate-100">
              <td className="py-2 pr-4 italic">“o crédito … de dd/mm ainda não está na conta — confirme o extrato”</td>
              <td className="py-2">
                O grupo ainda consta como agendado no arquivo e o crédito não foi encontrado. Consulte o extrato na
                Conciliação Itaú; se o crédito está lá como Novo, crie o lançamento e tente de novo.
              </td>
            </tr>
            <tr className="border-b border-slate-100">
              <td className="py-2 pr-4 italic">“alguma linha não existe, já foi aplicada ou é de outra empresa — recarregue a data”</td>
              <td className="py-2">
                Alguém (ou a baixa automática) já decidiu esse grupo. Clique no dia de novo para ver a situação atual.
              </td>
            </tr>
            <tr>
              <td className="py-2 pr-4 italic">“Há um buraco na cobertura …”</td>
              <td className="py-2">Exporte e importe o Detalhado exatamente das datas indicadas.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Section>

    {/* 11 */}
    <Section id="guia-glossario" icon={<CircleHelp size={18} />} title="11. Glossário">
      <dl className="grid grid-cols-1 md:grid-cols-[10rem_1fr] gap-x-4 gap-y-2">
        <dt className="font-medium text-slate-800">Agenda / previsto</dt>
        <dd>Parcelas que o sistema espera receber, geradas a partir das vendas no cartão.</dd>
        <dt className="font-medium text-slate-800">Grupo</dt>
        <dd>Data + bandeira + tipo (Venda crédito, Venda parcelada, Cancelamento). É a linha do detalhe do dia.</dd>
        <dt className="font-medium text-slate-800">Líquido</dt>
        <dd>Valor da venda menos a taxa da Cielo. É o que cai na conta e o que a conferência compara.</dd>
        <dt className="font-medium text-slate-800">TID / NSU</dt>
        <dd>Identificadores da transação na Cielo. É pelo TID que o sistema liga cada pagamento à parcela prevista.</dd>
        <dt className="font-medium text-slate-800">Detalhado × Resumo</dt>
        <dd>
          Os dois relatórios do portal da Cielo. O Detalhado traz venda a venda, com TID, e é o recomendado. O Resumo só
          traz totais.
        </dd>
        <dt className="font-medium text-slate-800">Fora da agenda</dt>
        <dd>Venda paga pela Cielo sem parcela prevista para aquele dia (venda antiga, agenda incompleta ou data deslocada).</dd>
        <dt className="font-medium text-slate-800">Arredondamento</dt>
        <dd>
          Diferença de centavos entre o rateio das parcelas e o que a Cielo paga. Aparece em cinza e não exige ação.
        </dd>
        <dt className="font-medium text-slate-800">Baixar</dt>
        <dd>Dar a parcela como recebida e registrar o dinheiro na conta, reaproveitando o crédito do extrato quando já existe.</dd>
      </dl>
    </Section>
  </div>
);
